var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * @deprecated Use the new Brushaway molecule from `@ui` instead:
 *
 * ```tsx
 * import { BrushawayProvider, Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui';
 * ```
 *
 * The new component removes the `isForm`/`onSubmit` pattern — compose your own
 * footer buttons using `BrushawayFooter` and `Button` instead.
 *
 * This file will be removed once all consumers have migrated.
 */
import React, { useRef, useEffect, useContext } from 'react';
import useHideUserSupportBtn from '@hooks/useHideUserSupportBtn';
import { Button } from '@ui';
import I18n from '../../utils/i18n';
import { BrushawayContext } from './BrushawayContext';
import './styles.scss';
function Brushaway({ anchor, title, children, isForm = false, onSubmit, submitButtonText = I18n.t('components.brushaway.save'), footerClassName, minWidth = 400, wrapperContentClassName, customFooter, isValid, disableCloseOnClickOutside }) {
    const { isOpen, buttonsDisabled, toggleBrushaway, closeBrushaway, setButtonsDisabled } = useContext(BrushawayContext);
    const brushawayRef = useRef(null);
    const isMounted = useRef(true);
    useHideUserSupportBtn(isOpen !== null && isOpen !== void 0 ? isOpen : false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        const handleKeyUp = (event) => {
            if (event.key === 'Escape' && isOpen) {
                closeBrushaway();
            }
        };
        const handleClickOutside = (event) => {
            const target = event.target;
            if (brushawayRef.current &&
                !brushawayRef.current.contains(target) &&
                target.closest('a, button') &&
                target.closest('[data-allow-click]') === null &&
                disableCloseOnClickOutside) {
                closeBrushaway();
            }
        };
        window.addEventListener('keyup', handleKeyUp);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, closeBrushaway]);
    const handleFormSubmit = (event) => __awaiter(this, void 0, void 0, function* () {
        event.preventDefault();
        setButtonsDisabled(true);
        if (onSubmit) {
            const result = yield onSubmit();
            if (isMounted.current) {
                setButtonsDisabled(false);
                if (result) {
                    closeBrushaway();
                }
            }
        }
        else {
            setButtonsDisabled(false);
        }
    });
    return (React.createElement(React.Fragment, null,
        anchor && anchor({ onClick: toggleBrushaway }),
        React.createElement("div", { id: "brushaway", className: `qmb-dialog ${isOpen ? '--active' : ''} brushaway-dialog`, style: {
                display: isOpen ? 'flex' : 'none',
                position: 'fixed',
                top: 0,
                right: 0,
                height: '100%',
                minWidth: `${minWidth}px`
            }, role: "dialog", "aria-labelledby": "brushaway-title", ref: brushawayRef, "data-allow-click": true },
            React.createElement("div", { className: "qmb-modal brushaway-modal" },
                React.createElement("h4", { id: "brushaway-title", className: "brushaway-title" }, title),
                React.createElement(Button, { onClick: closeBrushaway, title: I18n.t('components.brushaway.close'), tabIndex: -1 },
                    React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" }),
                    React.createElement("span", { className: "sr-only" }, I18n.t('components.brushaway.close')))),
            React.createElement("div", { className: "dialog__body brushaway-body" },
                React.createElement("section", { className: wrapperContentClassName }, children)),
            React.createElement("div", { className: `brushaway-footer ${footerClassName !== null && footerClassName !== void 0 ? footerClassName : ''}`, style: {
                    minWidth: `${minWidth}px`
                } }, customFooter !== null && customFooter !== void 0 ? customFooter : (isForm ? (React.createElement(React.Fragment, null,
                React.createElement(Button, { className: "mr-1", onClick: closeBrushaway, disabled: buttonsDisabled }, I18n.t('components.brushaway.cancel')),
                React.createElement(Button, { variant: "primary", onClick: (e) => {
                        handleFormSubmit(e).catch((err) => {
                            // shouldn't ever be triggered because handleFormSubmit
                            // should manage errors
                            console.error('Form submission failed', err);
                        });
                    }, disabled: buttonsDisabled || !isValid }, submitButtonText))) : (React.createElement(Button, { variant: "highlighted", onClick: closeBrushaway }, I18n.t('components.brushaway.close'))))))));
}
Brushaway.defaultProps = {
    anchor: undefined,
    children: null,
    isForm: false,
    onSubmit: undefined,
    submitButtonText: I18n.t('components.brushaway.save'),
    disableCloseOnClickOutside: false,
    wrapperContentClassName: '',
    footerClassName: '',
    minWidth: 400,
    customFooter: null
};
export default Brushaway;
